import { useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useHttp from '../../../hooks/useHttp';

// Common attribution origins from the e-mail marketing concept; free text is
// allowed as well.
const ORIGIN_OPTIONS = ['welcome_flow', 'birthday_flow', 'escape_special'];

// Settings for the "Gutschein erstellen" block: value, validity, attribution
// origin, campaign-code prefix and the per-entertainment mode of the concept
// ("10 € je Entertainment").
const IssueVoucherSettings = ({ block, readOnly, onConfigChange }) => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();
  const config = block.config || {};
  const [entertainments, setEntertainments] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await sendRequest({
          url: '/api/v1/entertainment',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { light: true },
        });
        if (!active) return;
        setEntertainments(response?.entertainments || response || []);
      } catch {
        if (active) toast.error('Fehler beim Laden der Entertainments.');
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedEntertainments = entertainments.filter(entertainment =>
    (config.entertainmentIds || []).some(id => String(id) === String(entertainment._id))
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          key={`${block.blockId}-amount`}
          size='small'
          type='number'
          label='Wert (€)'
          defaultValue={Number(config.amount) || 10}
          disabled={readOnly}
          inputProps={{ min: 1 }}
          onChange={event => {
            const parsed = Number(event.target.value);
            onConfigChange({ amount: Number.isFinite(parsed) && parsed > 0 ? parsed : 10 });
          }}
          sx={{ width: 110 }}
        />
        <TextField
          key={`${block.blockId}-validity`}
          size='small'
          type='number'
          label='Gültig (Tage)'
          defaultValue={Number(config.validityDays) || 30}
          disabled={readOnly}
          inputProps={{ min: 1 }}
          onChange={event => {
            const parsed = parseInt(event.target.value, 10);
            onConfigChange({ validityDays: Number.isNaN(parsed) ? 30 : Math.max(1, parsed) });
          }}
          sx={{ flex: 1 }}
        />
      </Box>
      <TextField
        select
        size='small'
        label='Herkunft (Attribution)'
        value={ORIGIN_OPTIONS.includes(config.origin) ? config.origin : config.origin || 'welcome_flow'}
        disabled={readOnly}
        onChange={event => onConfigChange({ origin: event.target.value })}
      >
        {ORIGIN_OPTIONS.map(origin => (
          <MenuItem key={origin} value={origin}>
            {origin}
          </MenuItem>
        ))}
        {config.origin && !ORIGIN_OPTIONS.includes(config.origin) && (
          <MenuItem value={config.origin}>{config.origin}</MenuItem>
        )}
      </TextField>
      <TextField
        key={`${block.blockId}-prefix`}
        size='small'
        label='Code-Präfix (z. B. WEL, BDAY)'
        defaultValue={config.codePrefix || ''}
        disabled={readOnly}
        onChange={event => onConfigChange({ codePrefix: event.target.value.trim().toUpperCase() })}
      />
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(config.perEntertainment)}
            disabled={readOnly}
            onChange={event => onConfigChange({ perEntertainment: event.target.checked })}
          />
        }
        label='Je Entertainment ein Gutschein'
      />
      <Autocomplete
        multiple
        size='small'
        options={entertainments}
        value={selectedEntertainments}
        disabled={readOnly}
        onChange={(event, value) =>
          onConfigChange({ entertainmentIds: value.map(entertainment => entertainment._id) })
        }
        getOptionLabel={option => option?.longName || option?.entertainmentName || ''}
        isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
        renderInput={params => (
          <TextField {...params} label='Entertainments (leer = alle)' placeholder='Suchen…' />
        )}
      />
      <Typography variant='caption' color='text.secondary'>
        Die Gutscheine werden dem Kontakt zugeordnet (E-Mail-Bindung) und können in einem
        „Nachricht senden“-Block angehängt werden. Der Kampagnen-Code entsteht aus Präfix,
        Entertainment-Kürzel, Wert und Monat (z. B. WEL-LZ-EUR10-AUG26).
      </Typography>
    </Box>
  );
};

export default IssueVoucherSettings;
